import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const KINDS = [
  {
    id: "front",
    title: "License Front",
    hint: "Photo of the front of your driver's license or state-issued ID.",
    icon: "fa-id-card",
  },
  {
    id: "back",
    title: "License Back",
    hint: "Flip the ID over and snap the back. Same person, same lighting.",
    icon: "fa-id-badge",
  },
  {
    id: "selfie",
    title: "Selfie With ID",
    hint: "Hold the same ID up next to your face. Matches you to the document.",
    icon: "fa-user-shield",
  },
];

const EVENT_TYPES = [
  { id: "wedding", label: "Wedding", icon: "fa-ring" },
  { id: "birthday", label: "Birthday", icon: "fa-cake-candles" },
  { id: "graduation", label: "Graduation", icon: "fa-graduation-cap" },
  { id: "corporate", label: "Corporate", icon: "fa-briefcase" },
  { id: "church-school", label: "Church / School", icon: "fa-church" },
  { id: "bbq-backyard", label: "BBQ / Backyard", icon: "fa-fire-flame-curved" },
  { id: "dinner-party", label: "Dinner Party", icon: "fa-utensils" },
  { id: "house-party", label: "House Party", icon: "fa-home" },
  { id: "other", label: "Other", icon: "fa-ellipsis" },
];

export default function Profile() {
  const { user, loading, refresh } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const fromCheckout = params.get("from") === "checkout";

  useEffect(() => {
    if (!loading && !user) navigate("/login?next=/profile");
  }, [loading, user, navigate]);

  if (loading || !user) return null;

  const verified = !!user.id_verified;

  return (
    <div className="bg-brand-slate min-h-screen">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 py-10">
        <div className="mb-8">
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-2">
            Account Verification
          </p>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tightest text-brand-blue">
            One-Time ID Check
          </h1>
          <p className="text-slate-500 font-bold text-sm mt-3 max-w-2xl">
            Self-service rentals require a quick photo-ID match. Upload all three below and you're
            verified for life — no need to repeat at future bookings.
          </p>
          {fromCheckout && !verified && (
            <div
              className="mt-5 rounded-2xl border-2 border-brand-gold bg-brand-gold/10 px-5 py-4 flex items-start gap-3"
              data-testid="profile-checkout-banner"
            >
              <i className="fas fa-bolt text-brand-blue text-lg mt-0.5" />
              <div>
                <p className="font-black uppercase text-xs text-brand-blue tracking-tightest">
                  Payment confirmed — last step
                </p>
                <p className="text-[11px] text-slate-700 font-medium mt-0.5 leading-snug">
                  Finish your one-time ID check below to activate your PIN immediately. You won't be
                  asked again on future rentals.
                </p>
              </div>
            </div>
          )}
          {verified && (
            <div
              className="mt-5 rounded-2xl border-2 border-emerald-200 bg-emerald-50 px-5 py-4 flex items-center gap-3"
              data-testid="profile-verified-banner"
            >
              <i className="fas fa-circle-check text-emerald-600 text-2xl" />
              <div>
                <p className="font-black uppercase text-xs text-emerald-800 tracking-tightest">
                  Identity verified
                </p>
                <p className="text-[11px] text-emerald-700 font-medium mt-0.5">
                  You're set up for instant rentals — no future ID re-uploads needed.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-3 gap-5">
          {KINDS.map((k) => (
            <UploadCard key={k.id} kind={k} user={user} onDone={refresh} />
          ))}
        </div>

        <CRMSection user={user} onSaved={refresh} />

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <Link
            to="/dashboard"
            className="btn-primary px-6 py-3 rounded-xl font-black uppercase text-xs tracking-widest text-center"
            data-testid="profile-go-dashboard"
          >
            <i className="fas fa-gauge mr-2" /> Back to Dashboard
          </Link>
          <Link
            to="/#inventory"
            className="px-6 py-3 rounded-xl border-2 border-brand-blue text-brand-blue font-black uppercase text-xs tracking-widest text-center hover:bg-brand-blue hover:text-white transition-colors"
            data-testid="profile-browse-link"
          >
            <i className="fas fa-shopping-basket mr-2" /> Browse Pod
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function UploadCard({ kind, user, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const fieldKey = {
    front: "id_document_path",
    back: "id_document_back_path",
    selfie: "selfie_path",
  }[kind.id];
  const uploaded = !!user[fieldKey];

  const choose = () => ref.current?.click();

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max file size is 5MB.");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/auth/me/id-document?kind=${kind.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${kind.title} uploaded`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div
      className={`bg-white rounded-[24px] p-5 border-2 transition-colors ${
        uploaded ? "border-emerald-200" : "border-slate-100"
      }`}
      data-testid={`profile-upload-card-${kind.id}`}
    >
      <div
        className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-3 ${
          uploaded ? "bg-emerald-100 text-emerald-700" : "bg-brand-blue text-brand-gold"
        }`}
      >
        <i className={`fas ${uploaded ? "fa-circle-check" : kind.icon} text-lg`} />
      </div>
      <p className="font-black text-sm uppercase text-brand-blue tracking-tightest">
        {kind.title}
      </p>
      <p className="text-[11px] font-medium text-slate-500 mt-1.5 leading-snug min-h-[44px]">
        {kind.hint}
      </p>
      <input
        ref={ref}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        capture={kind.id === "selfie" ? "user" : undefined}
        onChange={(e) => upload(e.target.files?.[0])}
        className="hidden"
        data-testid={`profile-upload-input-${kind.id}`}
      />
      <button
        onClick={choose}
        disabled={busy}
        className={`mt-3 w-full py-2.5 rounded-xl font-black uppercase tracking-widest text-[10px] transition-colors ${
          uploaded
            ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            : "btn-primary"
        } disabled:opacity-50`}
        data-testid={`profile-upload-button-${kind.id}`}
      >
        {busy ? (
          <>
            <i className="fas fa-spinner fa-spin mr-2" /> Uploading
          </>
        ) : uploaded ? (
          <>
            <i className="fas fa-rotate mr-2" /> Replace
          </>
        ) : (
          <>
            <i className="fas fa-upload mr-2" /> Upload
          </>
        )}
      </button>
    </div>
  );
}



function CRMSection({ user, onSaved }) {
  const [address, setAddress] = useState(user.service_address || "");
  const [eventTypes, setEventTypes] = useState(user.event_types || []);
  const [eventOther, setEventOther] = useState(user.event_type_other || "");
  const [wishlist, setWishlist] = useState(
    (user.equipment_wishlist || []).join("\n")
  );
  const [newsletterOpt, setNewsletterOpt] = useState(user.newsletter_opt_in !== false);
  const [smsOpt, setSmsOpt] = useState(user.sms_opt_in !== false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setAddress(user.service_address || "");
    setEventTypes(user.event_types || []);
    setEventOther(user.event_type_other || "");
    setWishlist((user.equipment_wishlist || []).join("\n"));
    setNewsletterOpt(user.newsletter_opt_in !== false);
    setSmsOpt(user.sms_opt_in !== false);
  }, [user]);

  const toggle = (id) =>
    setEventTypes((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const save = async () => {
    setBusy(true);
    try {
      const wl = wishlist
        .split(/[,\n]/)
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 8);
      await api.patch("/auth/me", {
        service_address: address || null,
        event_types: eventTypes,
        event_type_other:
          eventTypes.includes("other") && eventOther ? eventOther : null,
        equipment_wishlist: wl,
        newsletter_opt_in: newsletterOpt,
        sms_opt_in: smsOpt,
      });
      toast.success("Profile updated");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-10" data-testid="profile-crm-section">
      <div className="mb-6">
        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-1.5">
          Tell Us About You
        </p>
        <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tightest text-brand-blue">
          Your Event Profile
        </h2>
        <p className="text-slate-500 font-bold text-sm mt-1.5 max-w-2xl">
          Helps us recommend the right gear and only send messages you actually want.
        </p>
      </div>

      <div className="bg-white rounded-[28px] border border-slate-100 shadow-sm p-6 space-y-7">
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-2">
            Home / Service Address
          </label>
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="1234 Main St, Denver, CO 80206"
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10"
            data-testid="profile-address-input"
          />
          {user.distance_miles_from_pod != null && (
            <p className="text-[11px] font-bold text-emerald-700 mt-2">
              <i className="fas fa-location-dot mr-1.5" />
              {user.distance_miles_from_pod} miles from the Pod
            </p>
          )}
          <p className="text-[10px] font-bold text-slate-400 mt-1">
            Used only to estimate distance + suggest nearby drop-off windows. Not shared.
          </p>
        </div>

        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-brand-blue mb-2.5">
            What kind of events do you host?
          </p>
          <div className="flex flex-wrap gap-2">
            {EVENT_TYPES.map((et) => {
              const on = eventTypes.includes(et.id);
              return (
                <button
                  key={et.id}
                  onClick={() => toggle(et.id)}
                  className={`px-3.5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border-2 transition-colors flex items-center gap-1.5 ${
                    on
                      ? "bg-brand-blue text-white border-brand-blue"
                      : "bg-white text-brand-blue border-slate-200 hover:border-brand-blue"
                  }`}
                  data-testid={`profile-event-${et.id}`}
                >
                  <i className={`fas ${et.icon}`} /> {et.label}
                </button>
              );
            })}
          </div>
          {eventTypes.includes("other") && (
            <input
              value={eventOther}
              onChange={(e) => setEventOther(e.target.value)}
              placeholder="Tell us about it..."
              className="mt-3 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10"
              data-testid="profile-event-other-input"
            />
          )}
        </div>

        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-brand-blue mb-2">
            Equipment You Wish We Carried
          </p>
          <textarea
            value={wishlist}
            onChange={(e) => setWishlist(e.target.value)}
            rows={3}
            placeholder={"e.g. Patio heaters\nCocktail bar\nKaraoke machine"}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10 resize-none"
            data-testid="profile-wishlist-textarea"
          />
          <p className="text-[10px] font-bold text-slate-400 mt-1">
            One per line. Up to 8. We use this to plan our next equipment purchases.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <label
            className={`flex items-start gap-3 rounded-2xl border-2 p-4 cursor-pointer transition-colors ${
              newsletterOpt
                ? "border-brand-blue bg-brand-blue/5"
                : "border-slate-200 hover:border-brand-blue/40"
            }`}
            data-testid="profile-newsletter-toggle"
          >
            <input
              type="checkbox"
              checked={newsletterOpt}
              onChange={(e) => setNewsletterOpt(e.target.checked)}
              className="mt-0.5 accent-brand-blue w-5 h-5"
              data-testid="profile-newsletter-checkbox"
            />
            <div>
              <p className="font-black text-xs uppercase text-brand-blue tracking-tightest">
                <i className="fas fa-envelope mr-2" />
                Monthly Newsletter
              </p>
              <p className="text-[11px] text-slate-500 font-medium mt-1 leading-snug">
                Seasonal event ideas, gear announcements, and Pod-only deals. One email/month.
              </p>
            </div>
          </label>
          <label
            className={`flex items-start gap-3 rounded-2xl border-2 p-4 cursor-pointer transition-colors ${
              smsOpt
                ? "border-brand-blue bg-brand-blue/5"
                : "border-slate-200 hover:border-brand-blue/40"
            }`}
            data-testid="profile-sms-toggle"
          >
            <input
              type="checkbox"
              checked={smsOpt}
              onChange={(e) => setSmsOpt(e.target.checked)}
              className="mt-0.5 accent-brand-blue w-5 h-5"
              data-testid="profile-sms-checkbox"
            />
            <div>
              <p className="font-black text-xs uppercase text-brand-blue tracking-tightest">
                <i className="fas fa-mobile-alt mr-2" />
                SMS Reminders
              </p>
              <p className="text-[11px] text-slate-500 font-medium mt-1 leading-snug">
                PIN delivery + pickup reminders only. Reply STOP anytime.
              </p>
            </div>
          </label>
        </div>

        <button
          onClick={save}
          disabled={busy}
          className="btn-primary w-full sm:w-auto px-8 py-3.5 rounded-2xl font-black uppercase tracking-widest text-sm disabled:opacity-50"
          data-testid="profile-save-button"
        >
          {busy ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2" /> Saving
            </>
          ) : (
            <>
              <i className="fas fa-floppy-disk mr-2" /> Save Profile
            </>
          )}
        </button>
      </div>
    </section>
  );
}
